import { useCallback, useEffect, useRef } from 'react';

interface AnimateSizeProps {
	children: React.ReactNode;
	animateHeight?: boolean;
	animateWidth?: boolean;
	className?: string;
	style?: React.CSSProperties;
}

export const AnimateSize = ({
	children,
	animateHeight = true,
	animateWidth = false,
	className = '',
	style = {},
}: AnimateSizeProps) => {
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const contentRef = useRef<HTMLDivElement | null>(null);

	const updateDimensions = useCallback(() => {
		if (wrapperRef.current && contentRef.current) {
			const { offsetWidth, offsetHeight } = contentRef.current;
			if (animateWidth) {
				wrapperRef.current.style.width = `${offsetWidth}px`;
			}
			if (animateHeight) {
				wrapperRef.current.style.height = `${offsetHeight}px`;
			}
		}
	}, [animateHeight, animateWidth]);

	useEffect(() => {
		const observer = new MutationObserver(updateDimensions);
		const resizeObserver = new ResizeObserver(updateDimensions);

		if (contentRef.current) {
			observer.observe(contentRef.current, {
				childList: true,
				subtree: true,
				attributes: true,
				characterData: true,
			});
			resizeObserver.observe(contentRef.current);
		}

		return () => {
			observer.disconnect();
			resizeObserver.disconnect();
		};
	}, [updateDimensions]);

	return (
		<div
			ref={wrapperRef}
			className={className}
			style={{
				...style,
				transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
			}}
		>
			<div ref={contentRef} style={{ width: 'auto', height: 'auto' }}>
				{children}
			</div>
		</div>
	);
};
